/**
 * Shared KaTeX Preview Components for BayesPrep
 * 
 * Reusable React components for LaTeX preview functionality
 * across all portals (Admin, Student, Super Admin)
 */

import React from 'react';
import { renderLatex, getThemeClasses } from './index.jsx';

/**
 * Individual KaTeX preview component with always-visible preview
 *
 * @param {Object} props
 * @param {string} props.text - Text content to render with LaTeX
 * @param {string} props.title - Title for the preview section
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.placeholder - Placeholder text when content is empty
 * @param {string} props.portal - Portal theme (ADMIN, STUDENT, SUPER_ADMIN)
 */
export const KatexPreview = ({
  text,
  title = 'Preview',
  className = '',
  placeholder = 'Preview will appear here...',
  portal = 'ADMIN'
}) => {
  const themeClasses = getThemeClasses(portal);

  return (
    <div className={`border border-slate-200 rounded-lg bg-white ${className}`}>
      <div className="p-3 border-b border-slate-200 bg-slate-50">
        <h4 className="text-sm font-medium text-slate-700">{title}</h4>
      </div>
      <div className="p-4">
        <div className="min-h-[60px] p-3 bg-slate-50 rounded border border-slate-200">
          {text?.trim() ? (
            <div className="text-sm leading-relaxed">
              {renderLatex(text)}
            </div>
          ) : (
            <div className="text-slate-400 italic text-sm">
              {placeholder}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Comprehensive preview panel for questions with options and explanation
 *
 * @param {Object} props
 * @param {string} props.questionText - Question text content
 * @param {string} props.questionImageUrl - Question image URL
 * @param {Array} props.options - Array of option objects with text, isCorrect, and imageUrl properties
 * @param {string} props.explanation - Explanation text content
 * @param {string} props.explanationImageUrl - Explanation image URL
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.portal - Portal theme (ADMIN, STUDENT, SUPER_ADMIN)
 */
export const KatexPreviewPanel = ({
  questionText,
  questionImageUrl,
  options = [],
  explanation,
  explanationImageUrl,
  className = '',
  portal = 'ADMIN'
}) => {
  const themeClasses = getThemeClasses(portal);

  // Debug logging
  console.log('KatexPreviewPanel received options:', options.map(opt => ({
    text: opt.text?.substring(0, 20),
    imageUrl: opt.imageUrl,
    hasImageUrl: !!opt.imageUrl
  })));

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Question Preview */}
      <div className="border border-slate-200 rounded-lg bg-white">
        <div className="p-3 border-b border-slate-200 bg-slate-50">
          <h4 className="text-sm font-medium text-slate-700">Question Preview</h4>
        </div>
        <div className="p-4">
          {questionText?.trim() ? (
            <div className="text-sm leading-relaxed">
              {renderLatex(questionText)}
            </div>
          ) : (
            <div className="text-slate-400 italic text-sm">
              Question text preview will appear here...
            </div>
          )}
          {/* Question Image */}
          {questionImageUrl && (
            <div className="mt-3">
              <img
                src={questionImageUrl}
                alt="Question"
                className="max-w-full h-auto rounded-lg shadow-sm max-h-64"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Options Preview */}
      {options.length > 0 && (
        <div className="border border-slate-200 rounded-lg bg-white">
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <h4 className="text-sm font-medium text-slate-700">Options Preview</h4>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {options.map((option, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${
                    option.isCorrect 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-slate-600 mt-0.5 min-w-[20px]">
                      {String.fromCharCode(65 + index)}:
                    </span>
                    <div className="flex-1">
                      {option.text?.trim() ? (
                        <div className="text-sm leading-relaxed">
                          {renderLatex(option.text)}
                        </div>
                      ) : (
                        <div className="text-slate-400 italic text-sm">
                          Option {String.fromCharCode(65 + index)} preview...
                        </div>
                      )}
                      {/* Option Image */}
                      {option.imageUrl && (
                        <div className="mt-2">
                          <img
                            src={option.imageUrl}
                            alt={`Option ${String.fromCharCode(65 + index)}`}
                            className="max-w-full h-auto rounded-lg shadow-sm max-h-48 border border-gray-300"
                          />
                        </div>
                      )}
                    </div>
                    {option.isCorrect && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                        Correct
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Explanation Preview */}
      {explanation && (
        <div className="border border-slate-200 rounded-lg bg-white">
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <h4 className="text-sm font-medium text-slate-700">Explanation Preview</h4>
          </div>
          <div className="p-4">
            {explanation?.trim() ? (
              <div className="text-sm leading-relaxed">
                {renderLatex(explanation)}
              </div>
            ) : (
              <div className="text-slate-400 italic text-sm">
                Explanation preview will appear here...
              </div>
            )}
            {/* Explanation Image */}
            {explanationImageUrl && (
              <div className="mt-3">
                <img
                  src={explanationImageUrl}
                  alt="Explanation"
                  className="max-w-full h-auto rounded-lg shadow-sm max-h-64"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Simple inline preview component for smaller spaces
 * 
 * @param {Object} props
 * @param {string} props.text - Text content to render
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.placeholder - Placeholder text when content is empty
 */
export const InlineKatexPreview = ({ 
  text, 
  className = '', 
  placeholder = 'No content' 
}) => {
  if (!text?.trim()) {
    return (
      <div className={`text-slate-400 italic text-sm ${className}`}>
        {placeholder}
      </div>
    );
  }

  return (
    <div className={`text-sm leading-relaxed ${className}`}>
      {renderLatex(text)}
    </div>
  );
};

/**
 * Card-based preview component with theme support
 * Suitable for admin portals with card-based layouts
 *
 * @param {Object} props
 * @param {string} props.text - Text content to render
 * @param {string} props.title - Card title
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.placeholder - Placeholder text when content is empty
 * @param {string} props.portal - Portal theme
 * @param {React.Component} props.CardComponent - Card wrapper component
 * @param {React.Component} props.CardHeaderComponent - Card header component
 * @param {React.Component} props.CardContentComponent - Card content component
 * @param {React.Component} props.CardTitleComponent - Card title component
 */
export const KatexPreviewCard = ({
  text,
  title = 'Preview',
  className = '',
  placeholder = 'Preview will appear here...',
  portal = 'ADMIN',
  CardComponent,
  CardHeaderComponent,
  CardContentComponent,
  CardTitleComponent
}) => {
  const themeClasses = getThemeClasses(portal);

  // Fallback to div-based layout if card components not provided
  if (!CardComponent) {
    return (
      <KatexPreview
        text={text}
        title={title}
        className={className}
        placeholder={placeholder}
        portal={portal}
      />
    );
  }

  return (
    <CardComponent className={className}>
      <CardHeaderComponent className="pb-2">
        <CardTitleComponent className="text-sm font-medium">{title}</CardTitleComponent>
      </CardHeaderComponent>
      <CardContentComponent className="pt-0">
        <div className="min-h-[60px] p-3 bg-muted/30 rounded-md border">
          {text?.trim() ? (
            <div className="text-sm leading-relaxed">
              {renderLatex(text)}
            </div>
          ) : (
            <div className="text-muted-foreground italic text-sm">
              {placeholder}
            </div>
          )}
        </div>
      </CardContentComponent>
    </CardComponent>
  );
};

/**
 * Comprehensive preview panel using card components
 * Suitable for admin portals with shadcn/ui or similar component libraries
 */
export const KatexPreviewCardPanel = ({
  questionText,
  options = [],
  explanation,
  className = '',
  portal = 'ADMIN',
  CardComponent,
  CardHeaderComponent,
  CardContentComponent,
  CardTitleComponent
}) => {
  const themeClasses = getThemeClasses(portal);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Question Preview */}
      <KatexPreviewCard
        text={questionText}
        title="Question Preview"
        placeholder="Question text preview will appear here..."
        portal={portal}
        CardComponent={CardComponent}
        CardHeaderComponent={CardHeaderComponent}
        CardContentComponent={CardContentComponent}
        CardTitleComponent={CardTitleComponent}
      />

      {/* Options Preview */}
      {options.length > 0 && CardComponent && (
        <CardComponent>
          <CardHeaderComponent className="pb-2">
            <CardTitleComponent className="text-sm font-medium">Options Preview</CardTitleComponent>
          </CardHeaderComponent>
          <CardContentComponent className="pt-0">
            <div className="space-y-2">
              {options.map((option, index) => (
                <div
                  key={index}
                  className={`p-2 rounded border ${
                    option.isCorrect 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-muted/30 border-border'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-medium text-muted-foreground mt-0.5">
                      {String.fromCharCode(65 + index)}:
                    </span>
                    <div className="flex-1">
                      {option.text?.trim() ? (
                        <div className="text-sm leading-relaxed">
                          {renderLatex(option.text)}
                        </div>
                      ) : (
                        <div className="text-muted-foreground italic text-sm">
                          Option {String.fromCharCode(65 + index)} preview...
                        </div>
                      )}
                    </div>
                    {option.isCorrect && (
                      <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                        Correct
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContentComponent>
        </CardComponent>
      )}

      {/* Explanation Preview */}
      {explanation && (
        <KatexPreviewCard
          text={explanation}
          title="Explanation Preview"
          placeholder="Explanation preview will appear here..."
          portal={portal}
          CardComponent={CardComponent}
          CardHeaderComponent={CardHeaderComponent}
          CardContentComponent={CardContentComponent}
          CardTitleComponent={CardTitleComponent}
        />
      )}
    </div>
  );
};
